import { CalendarMonthOutlined, InfoOutlined, WarningAmber } from '@mui/icons-material';
import { BaseTextFieldProps, Box, IconButton, InputAdornment, TextField, Typography } from '@mui/material';
import { DateTimePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterLuxon } from '@mui/x-date-pickers/AdapterLuxon';
import { styled } from '@mui/system';
import { DateTime } from 'luxon';
import WolfTooltip from './WolfTooltip';

const StyledTextField = styled(TextField, {
  shouldForwardProp: (prop) => prop !== 'borderless',
})<{ borderless?: string }>(({ theme, borderless }) => ({
  '& .MuiInputBase-root': {
    boxSizing: 'border-box',
    height: '40px',
    '& .MuiInputBase-input': {
      padding: '8px 16px',
      height: '40px',
      boxSizing: 'border-box',
    },
  },
  '& .MuiInputLabel-root': {
    display: 'none', // Hide the label by default
  },
  '& .MuiOutlinedInput-root': {
    border: borderless === 'true' ? 'none' : `1px solid ${theme.palette.primaryIcy.main}`,
    transition: 'border-color 0.3s ease',
    '&:hover': {
      border: borderless === 'true' ? 'none' : `1px solid ${theme.palette.primaryIcy.main}`,
    },
    '&.Mui-focused': {
      outline: 'none', // Remove default focus outline
    },
    '&.Mui-focused fieldset': {
      border: borderless === 'true' ? 'none' : `1px solid ${theme.palette.primary.main}`,
    },
    '&.Mui-error fieldset': {
      border: borderless === 'true' ? 'none' : `1px solid ${theme.palette.feedbackError.main}`,
    },
    '&.Mui-disabled fieldset': {
      color: theme.palette.primaryIcy.main,
      border: borderless === 'true' ? 'none' : `1px solid ${theme.palette.primaryIcy.main}`,
      background: theme.palette.action.disabledBackground,
    },
    '& fieldset': {
      border: 'none',
    },
  },
}));

interface WolfInputProps extends BaseTextFieldProps {
  label: string;
  secondaryLabel?: string;
  name?: string;
  placeholder?: string;
  tooltipText?: string;
  showIcon?: boolean;
  icon?: React.ReactNode;
  iconAction?: () => void;
  error?: boolean;
  errorMessage?: string;
  disabled?: boolean;
  boxFullWidth?: boolean;
  autoComplete?: string;
  autoFocus?: boolean;
  borderless?: string;
  onChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
  inputRef?: React.Ref<HTMLInputElement>;
  value?: any;
  inputProps?: any;
  type?: string;
  onPickerOpen?: () => void;
  onPickerClose?: () => void;
}

const WolfInput: React.FC<WolfInputProps> = ({
  label,
  secondaryLabel,
  name,
  placeholder,
  tooltipText,
  showIcon = false,
  icon,
  iconAction,
  error = false,
  errorMessage,
  disabled,
  boxFullWidth = true,
  autoComplete = 'off',
  autoFocus = false,
  borderless = 'false',
  onChange,
  inputRef,
  value,
  inputProps,
  type,
  onPickerOpen,
  onPickerClose,
  sx,
  classes,
  ...props
}) => {
  // Special handling for datetime-local inputs
  if (type === 'datetime-local') {
    return (
      <Box className={'flex flex-wrap justify-center ' + (boxFullWidth ? 'w-full' : '')}>
        {label && (
          <Box className={secondaryLabel ? 'flex w-full justify-between' : 'flex w-full justify-start'}>
            <Typography variant="body14semibold" gutterBottom className="w-full">
              {label}
              {tooltipText && (
                <WolfTooltip title={tooltipText}>
                  <IconButton size="small">
                    <InfoOutlined sx={{ width: 18, height: 18 }} className="text-primaryDark" />
                  </IconButton>
                </WolfTooltip>
              )}
            </Typography>
            {secondaryLabel && (
              <Typography variant="body14" className="text-additionalPlaceholder" color="additionalPlaceholder">
                {secondaryLabel}
              </Typography>
            )}
          </Box>
        )}
        <LocalizationProvider dateAdapter={AdapterLuxon}>
          <DateTimePicker
            value={value ? DateTime.fromISO(value) : null}
            onChange={(newValue) => {
              if (newValue && onChange) {
                const formattedValue = newValue.toFormat("yyyy-MM-dd'T'HH:mm");
                onChange({
                  target: { name: name || '', value: formattedValue },
                } as React.ChangeEvent<HTMLInputElement>);
              }
            }}
            onOpen={onPickerOpen}
            onClose={onPickerClose}
            disabled={disabled}
            slots={{
              textField: TextField,
            }}
            slotProps={{
              textField: {
                fullWidth: true,
                error: error,
                variant: 'outlined',
                sx: {
                  '& .MuiInputBase-root': {
                    height: '40px',
                    '& .MuiInputBase-input': {
                      padding: '8px 16px',
                      height: '40px',
                      boxSizing: 'border-box',
                    },
                  },
                  '& .MuiOutlinedInput-root': {
                    border: borderless === 'true' ? 'none' : '1px solid',
                    borderColor: 'primaryIcy.main',
                    borderRadius: '4px',
                    transition: 'border-color 0.3s ease',
                    '&:hover': {
                      borderColor: 'primaryIcy.main',
                    },
                    '&.Mui-focused': {
                      borderColor: 'primary.main',
                    },
                    '&.Mui-error': {
                      borderColor: 'feedbackError.main',
                    },
                    '& fieldset': {
                      border: 'none',
                    },
                  },
                },
              },
              popper: {
                sx: {
                  '& .MuiPaper-root': {
                    border: '2px solid',
                    borderColor: 'primary.main',
                    borderRadius: '8px',
                    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
                  },
                },
              },
              actionBar: {
                sx: {
                  '& .MuiButton-root': {
                    fontWeight: 600,
                  },
                  '& .MuiButton-root:last-child': {
                    backgroundColor: 'primary.main',
                    color: 'white',
                    '&:hover': {
                      backgroundColor: 'primary.dark',
                    },
                  },
                },
              },
            }}
            timeSteps={{ minutes: 15 }}
            closeOnSelect={false}
          />
        </LocalizationProvider>
        {error && errorMessage && (
          <Typography variant="body2" gutterBottom className="w-full text-feedbackError">
            <IconButton size="small">
              <WarningAmber sx={{ width: 16, height: 14 }} className="text-feedbackError" />
            </IconButton>
            {errorMessage}
          </Typography>
        )}
      </Box>
    );
  }

  return (
    <Box className={'flex flex-wrap justify-center ' + (boxFullWidth ? 'w-full' : '')}>
      {label && (
        <Box className={secondaryLabel ? 'flex w-full justify-between' : 'flex w-full justify-start'}>
          <Typography variant="body14semibold" gutterBottom className="w-full">
            {label}
            {tooltipText && (
              <WolfTooltip title={tooltipText}>
                <IconButton size="small">
                  <InfoOutlined sx={{ width: 18, height: 18 }} className="text-primaryDark" />
                </IconButton>
              </WolfTooltip>
            )}
          </Typography>
          {secondaryLabel && (
            <Typography variant="body14" className="text-additionalPlaceholder" color="additionalPlaceholder">
              {secondaryLabel}
            </Typography>
          )}
        </Box>
      )}
      <StyledTextField
        inputRef={inputRef}
        placeholder={placeholder}
        error={error}
        variant="outlined"
        type={type}
        onChange={onChange}
        onBlur={(event) => {
          const trimmedValue = event.target.value.trim();
          if (trimmedValue !== event.target.value) {
            onChange?.({
              ...event,
              target: { ...event.target, name: event.target.name, value: trimmedValue },
            } as unknown as React.ChangeEvent<HTMLInputElement>);
          }
        }}
        disabled={disabled}
        name={name}
        autoComplete={autoComplete}
        autoFocus={autoFocus}
        borderless={borderless}
        fullWidth
        value={value}
        InputProps={
          showIcon
            ? {
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton edge="end" onClick={iconAction}>
                      {icon ? (
                        icon
                      ) : (
                        <CalendarMonthOutlined sx={{ width: 22, height: 24 }} className="text-primaryDark" />
                      )}
                    </IconButton>
                  </InputAdornment>
                ),
                inputProps: inputProps,
              }
            : inputProps ? { inputProps: inputProps } : undefined
        }
        {...props}
      />
      {error && errorMessage && (
        <Typography variant="body2" gutterBottom className="w-full text-feedbackError">
          <IconButton size="small">
            <WarningAmber sx={{ width: 16, height: 14 }} className="text-feedbackError" />
          </IconButton>
          {errorMessage}
        </Typography>
      )}
    </Box>
  );
};

export default WolfInput;
